/**
 * Client side of the server-side Patient report batch
 * (functions/reports/patientReportBatch.js): run subscriptions, callables, and
 * chart-filed monthly summaries.
 */
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db, functions, httpsCallable } from '../firebase';

export { batchRunFingerprint } from '../utils/patientReportBatchFingerprint';

export const TENANT_WIDE_REPORT_ROLES = new Set(['admin', 'clinical admin', 'superuser']);
export const ACTIVE_BATCH_STATUSES = new Set(['queued', 'running', 'zipping']);
export const FINISHED_BATCH_STATUSES = new Set(['completed', 'completed_with_errors', 'failed']);
export const DOWNLOADABLE_BATCH_STATUSES = new Set(['completed', 'completed_with_errors']);

const STATUS_LABELS = {
  queued: 'Queued',
  running: 'Generating',
  zipping: 'Building ZIP',
  completed: 'Ready',
  completed_with_errors: 'Ready (with errors)',
  failed: 'Failed',
  canceled: 'Canceled',
  expired: 'Expired',
};

export function batchStatusLabel(status) {
  return STATUS_LABELS[status] || status || '';
}

export function isTenantWideReportRole(role) {
  return TENANT_WIDE_REPORT_ROLES.has(String(role || '').toLowerCase().trim());
}

function batchesCol(tenantId) {
  return collection(db, 'tenants', tenantId, 'patientReportBatches');
}

/**
 * Live list of recent runs visible to the caller. Tenant-wide roles see every
 * run; other roles only their own (rules require the createdByUid filter).
 */
export function subscribeRecentPatientReportBatches({ tenantId, uid, tenantWide, max = 25 }, onNext, onError) {
  if (!db || !tenantId || !uid) return () => {};
  const q = tenantWide
    ? query(batchesCol(tenantId), orderBy('createdAt', 'desc'), limit(max))
    : query(batchesCol(tenantId), where('createdByUid', '==', uid), orderBy('createdAt', 'desc'), limit(max));
  return onSnapshot(
    q,
    { includeMetadataChanges: false },
    (snap) => onNext(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (e) => onError?.(e),
  );
}

async function call(name, payload) {
  const fn = httpsCallable(functions, name);
  const res = await fn(payload || {});
  return res.data;
}

export function createPatientReportBatch(payload) {
  return call('createPatientReportBatch', payload);
}

export function cancelPatientReportBatch(batchId) {
  return call('cancelPatientReportBatch', { batchId });
}

export function regenerateMonthEndBatch(ym) {
  return call('regenerateMonthEndBatch', { ym });
}

function triggerDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function downloadPatientReportBatch(batchId) {
  const { url, filename } = await call('getPatientReportBatchDownloadUrl', { batchId });
  triggerDownload(url, filename);
}

export async function downloadPatientSummaryDocument({ patientId, ym, versionId }) {
  const { url, filename } = await call('getPatientSummaryDocumentUrl', { patientId, ym, versionId });
  triggerDownload(url, filename);
}

/**
 * Monthly summaries filed to a patient's chart, newest month first, each with
 * its versions (newest first).
 */
export async function listPatientSummaryReports(tenantId, patientId) {
  if (!db || !tenantId || !patientId) return [];
  const months = await getDocs(collection(db, 'tenants', tenantId, 'patients', patientId, 'patientSummaryReports'));
  const rows = await Promise.all(
    months.docs.map(async (m) => {
      const versionsSnap = await getDocs(
        query(collection(m.ref, 'versions'), orderBy('batchCreatedAtMs', 'desc')),
      );
      return {
        ym: m.id,
        ...m.data(),
        versions: versionsSnap.docs.map((v) => ({ id: v.id, ...v.data() })),
      };
    }),
  );
  return rows.sort((a, b) => b.ym.localeCompare(a.ym));
}

export function timestampMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts instanceof Date) return ts.getTime();
  return Number(ts) || 0;
}
